/**
 * Portal preview mode — agency admin's read-only impersonation of a contact.
 *
 * State lives in a tiny module-level holder + sessionStorage so it survives
 * navigation within the previewing tab. The header injection in
 * `lib/trpc.ts` reads from this on every request.
 *
 * Per-tab via sessionStorage (not localStorage) so that one tab can be the
 * agency view while another is the portal preview without crossfeeding.
 */

const STORAGE_KEY = 'scope:preview';

export interface PreviewState {
  contactId: string;
  contactName: string;
  contactEmail: string;
  contactRole: 'CLIENT_ADMIN' | 'CLIENT_VIEWER';
  clientId: string;
  clientName: string;
  agencyId: string;
}

type Listener = () => void;
const listeners = new Set<Listener>();

function notify(): void {
  for (const l of listeners) l();
}

export function getPreviewState(): PreviewState | null {
  try {
    if (typeof window === 'undefined') return null;
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PreviewState) : null;
  } catch {
    return null;
  }
}

export function getPreviewContactId(): string | null {
  return getPreviewState()?.contactId ?? null;
}

export function setPreview(state: PreviewState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* sessionStorage unavailable; preview won't survive reloads, but the
     * current navigation still works because callers also navigate. */
  }
  notify();
}

export function clearPreview(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  notify();
}

export function subscribePreview(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
